import React, { useState, useEffect } from 'react';
import visitorTracking from '../../services/visitorTracking';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';

const TrackingTest = () => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [firebaseTest, setFirebaseTest] = useState('testing...');
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [consentStatus, setConsentStatus] = useState('checking...');

  useEffect(() => {
    // Test device detection
    const testDeviceInfo = () => {
      const userAgent = navigator.userAgent;
      const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);
      
      let deviceType = 'Desktop';
      if (isMobile && !isTablet) {
        deviceType = 'Mobile';
      } else if (isTablet) {
        deviceType = 'Tablet';
      }

      return {
        type: deviceType,
        userAgent: userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        isMobile,
        isTablet
      };
    };

    setDeviceInfo(testDeviceInfo());

    // Check consent status
    const consent = localStorage.getItem('grey-cookie-consent');
    if (consent) {
      const consentData = JSON.parse(consent);
      setConsentStatus(`Analytics: ${consentData.analytics ? 'Enabled' : 'Disabled'}`);
    } else {
      setConsentStatus('No consent given yet');
    }

    // Test Firebase connection
    const testFirebase = async () => {
      try {
        const visitorsQuery = query(
          collection(db, 'visitor_tracking'),
          orderBy('timestamp', 'desc'),
          limit(5)
        );
        const snapshot = await getDocs(visitorsQuery);
        const visitors = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate?.() || 'No timestamp'
        }));
        setRecentVisitors(visitors);
        setFirebaseTest('Connected ✅');
      } catch (error) {
        console.error('Firebase test error:', error);
        setFirebaseTest(`Error: ${error.message}`);
      }
    };

    testFirebase();
  }, []);

  const testTrackVisit = async () => {
    try {
      // Set analytics consent temporarily for testing
      localStorage.setItem('grey-cookie-consent', JSON.stringify({
        necessary: true,
        analytics: true,
        marketing: false,
        functional: false
      }));
      
      // Force track a visit
      await visitorTracking.trackVisit();
      alert('Visit tracked! Check the admin dashboard.');
    } catch (error) {
      alert(`Error tracking visit: ${error.message}`);
    }
  };

  if (!deviceInfo) return <div>Loading...</div>;

  return (
    <div style={{ 
      padding: '20px', 
      fontFamily: 'monospace', 
      backgroundColor: '#f5f5f5',
      margin: '20px',
      borderRadius: '8px'
    }}>
      <h2>🔍 Visitor Tracking Debug</h2>
      
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '5px' }}>
        <h3>📱 Device Detection</h3>
        <p><strong>Device Type:</strong> {deviceInfo.type}</p>
        <p><strong>Is Mobile:</strong> {deviceInfo.isMobile ? 'Yes' : 'No'}</p>
        <p><strong>Is Tablet:</strong> {deviceInfo.isTablet ? 'Yes' : 'No'}</p>
        <p><strong>Screen:</strong> {deviceInfo.screenWidth}x{deviceInfo.screenHeight}</p>
        <p><strong>Viewport:</strong> {deviceInfo.viewportWidth}x{deviceInfo.viewportHeight}</p>
      </div>

      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '5px' }}>
        <h3>🍪 Cookie Consent Status</h3>
        <p>{consentStatus}</p>
      </div>

      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '5px' }}>
        <h3>🔥 Firebase Connection</h3>
        <p><strong>Status:</strong> {firebaseTest}</p>
        <p><strong>Recent Visitors Count:</strong> {recentVisitors.length}</p>
        {recentVisitors.length > 0 && (
          <div>
            <h4>Last 5 Visitors:</h4>
            {recentVisitors.map((visitor, index) => (
              <div key={visitor.id} style={{ 
                padding: '8px', 
                margin: '5px 0', 
                backgroundColor: '#f0f0f0',
                borderRadius: '3px',
                fontSize: '12px'
              }}>
                <strong>#{index + 1}</strong> - {visitor.deviceType} - {visitor.timestamp?.toString() || 'No timestamp'}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '5px' }}>
        <h3>🧪 Test Tracking</h3>
        <button 
          onClick={testTrackVisit}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Force Track Visit
        </button>
        <p style={{ fontSize: '12px', color: '#666' }}>
          This will temporarily enable analytics consent and track a visit
        </p>
      </div>

      <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '5px', border: '1px solid #ffeaa7' }}>
        <h3>📋 Instructions</h3>
        <ol>
          <li>Check that device detection shows the correct type</li>
          <li>Accept analytics cookies on the main site</li>
          <li>Visit different pages to generate tracking data</li>
          <li>Check the admin dashboard at /admin/analytics</li>
          <li>Use "Force Track Visit" button to test immediately</li>
        </ol>
      </div>
    </div>
  );
};

export default TrackingTest;